import JSZip from 'jszip';

/**
 * Hands fetched images to the user: the native share sheet on touch devices
 * (so images land in Photos), a ZIP everywhere else.
 *
 * files        - [{ blob, name }] from downloadAtTier
 * zipName      - filename for the desktop ZIP
 * onZipPercent - called with 0..100 while the ZIP is compressed
 *
 * Returns 'shared' | 'zipped' | 'cancelled'.
 */

// Capability is not enough on its own. Desktop Safari implements
// canShare({ files }), so a pure feature check sends a desktop download into a
// native share sheet that blocks until the user dismisses it -- no ZIP, and no
// progress to report while it waits. The sheet is only the right answer where
// it is the way images reach the photo library, which is a touch device.
function prefersShareSheet() {
  if (!navigator.canShare || !navigator.share) return false;
  if (typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(pointer: coarse)').matches;
}

export async function deliverFiles(files, { zipName, onZipPercent } = {}) {
  // A File needs a concrete type or the share sheet may reject the payload.
  const nativeFiles = files.map(
    ({ blob, name }) => new File([blob], name, { type: blob.type || 'image/jpeg' })
  );

  if (prefersShareSheet() && navigator.canShare({ files: nativeFiles })) {
    try {
      await navigator.share({ files: nativeFiles });
      return 'shared';
    } catch (err) {
      // The user dismissing the sheet is a decision, not a failure.
      if (err.name === 'AbortError') return 'cancelled';
      // Anything else (payload too large, unsupported type) falls through to
      // the ZIP so the download still completes.
    }
  }

  // Wrapping a lone image in a ZIP just to unwrap it is hostile. Hand it over
  // directly instead.
  if (files.length === 1) {
    saveBlob(files[0].blob, files[0].name);
    return 'zipped';
  }

  const zip = new JSZip();
  files.forEach(({ blob, name }) => zip.file(name, blob));

  const content = await zip.generateAsync({ type: 'blob' }, (meta) => {
    if (onZipPercent) onZipPercent(meta.percent);
  });

  saveBlob(content, zipName);
  return 'zipped';
}

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoking in the same tick can cancel the download before it starts.
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
